import css from '../index.module.css';

import SEO from "@components/seo";
import useUserData from "@hooks/useUserData";

import EmptyCart from "@pages/system/emptyCart";
import MainContent from "@components/content";
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';

export default function Checkout() {
  const { cartData, refreshCartData } = useUserData();
  const navigate = useNavigate();
  
  const [address, setAddress] = useState<string>("");
  const [comment, setComment] = useState<string>("");
  const [organization, setOrganization] = useState<string>("");
  const [addresses, setAddresses] = useState<any>([]);
  const [organizations, setOrganizations] = useState<any>([]);
  const [showAddressPopup, setShowAddressPopup] = useState<boolean>(false);
  const [showOrganizationPopup, setShowOrganizationPopup] = useState<boolean>(false);

  // Hide floating menu when popup is open
  useEffect(() => {
    if (showAddressPopup) {
      document.body.classList.add('address-popup-open');
    } else {
      document.body.classList.remove('address-popup-open');
    }
    
    return () => {
      document.body.classList.remove('address-popup-open');
    };
  }, [showAddressPopup]);

  const fetchAddresses = () => {
    axios.get(`${import.meta.env.VITE_API_URL}/addresses`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    })
    .then(response => {
      setAddresses(response.data);
    })
    .catch(error => {
      console.log(error);
    });
  };
  
  const fetchOrganizations = () => {
    axios.get(`${import.meta.env.VITE_API_URL}/organizations`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    })
    .then(response => {
      setOrganizations(response.data);
    })
    .catch(error => {
      console.log(error);
    });
  };

  useEffect(() => {
    fetchAddresses();
    fetchOrganizations();
  }, []);

  // Refresh addresses when page becomes visible (user returns from account page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchAddresses();
        fetchOrganizations();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', fetchAddresses);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', fetchAddresses);
    };
  }, []);

  const goToCheckout = (e: any) => {
    e.preventDefault();

    if (!organization || !address) {
      return toast.warning('Выберите организацию и адрес');
    }

    const data = {
      shop: address,
      address: address,
      orgId: organization,
      comment: comment
    }

    axios.post(`${import.meta.env.VITE_API_URL}/checkout`, data, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    }).then(response => {
      refreshCartData();
      navigate(`/webapp/order/${response.data.orderID}`);
      toast.success('Заказ оформлен, спасибо');
    }).catch(error => {
      console.log(error);
      if (error.response && error.response.status === 400) {
        toast.error('Скрытый товар нельзя заказать');
      } else {
        toast.error('Возникла ошибка');
      }
    });
  }

  const cartCount = Math.ceil(
    Array.isArray(cartData)
      ? cartData.reduce((sum: number, item: any) => sum + (Number(item?.count) || 0), 0)
      : 0
  );

  const totalPrice = Array.isArray(cartData)
    ? cartData.reduce((acc, cart) => acc + cart.product.price * Number(cart.count), 0)
    : 0;

  return (
  <>
    {(!cartData || cartData.length <= 0) ? 
      <EmptyCart /> :
    
    <MainContent header="Оформление заказа">    
      <SEO 
        title={`Оформление заказа`}
        description={`Оформление заказа`}
        ogImage={``}
        ogUrl={``}
      />

      <div className={css.cart} style={{ justifyContent: 'start' }}>
        <Link to={`/webapp/cart`} className={css.back}>Вернуться в корзину</Link>

        <div className={css.cartInfo} style={{ gap: 10 }}>
          <div className={css.total}>
            <div className={css.item}>
              <a>Выберите организацию</a>

              <div 
                className={css.addressSelector}
                onClick={() => setShowOrganizationPopup(true)}
              >
                {organization ? (
                  <div className={css.selectedAddress}>
                    <div className={css.selectedAddressText}>
                      {organizations.find((org: any) => org.id === organization)?.name || organization}
                    </div>
                    <div className={css.selectedAddressSubtext}>
                      {organizations.find((org: any) => org.id === organization)?.address || organization}
                    </div>
                  </div>
                ) : (
                  <div className={css.noAddressSelected}>
                    Выберите организацию
                  </div>
                )}
                <div className={css.addressSelectorArrow}>›</div>
              </div>
            </div>

            {showOrganizationPopup && (
              <div className={css.addressPopup}>
                <div className={css.addressPopupBackdrop} onClick={() => setShowOrganizationPopup(false)} />
                <div className={css.addressPopupContent}>
                  <div className={css.addressPopupHeader}>
                    <h2>Выберите организацию</h2>
                    <button 
                      className={css.addressPopupClose}
                      onClick={() => setShowOrganizationPopup(false)}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className={css.addressPopupList}>
                    {organizations.length > 0 ? (
                      organizations.map((org: any) => (
                        <div
                          key={org.id}
                          className={`${css.addressPopupCard} ${organization === org.id ? css.addressPopupCardActive : ''}`}
                          onClick={() => {
                            setOrganization(org.id);
                            setShowOrganizationPopup(false);
                          }}
                        >
                          <div className={css.addressPopupCardContent}>
                            <div className={css.addressPopupCardTitle} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{org.status === 'ACTIVE' ? <div className={css.circleActive} /> : <div className={css.circleInactive} />} {org.name}</div>
                            <div className={css.addressPopupCardAddress}>{org.address}</div>
                          </div>
                          {organization === org.id && (
                            <div className={css.addressPopupCardCheck}>✓</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className={css.noAddressesMessage}>
                        У вас нет сохраненных организаций
                      </div>
                    )}
                    
                    <button
                      type="button"
                      className={css.createAddressBtn}
                      onClick={() => {
                        setShowOrganizationPopup(false);
                        navigate('/webapp/user', { state: { page: 6 } });
                      }}
                    >
                      + Создать организацию
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={css.total}>
            <div className={css.item}>
              <a>Адрес доставки</a>

              <div 
                className={css.addressSelector}
                onClick={() => setShowAddressPopup(true)}
              >
                {address ? (
                  <div className={css.selectedAddress}>
                    <div className={css.selectedAddressText}>
                      {addresses.find((addr: any) => addr.address === address)?.title || address}
                    </div>
                    <div className={css.selectedAddressSubtext}>
                      {addresses.find((addr: any) => addr.address === address)?.address || address}
                    </div>
                  </div>
                ) : (
                  <div className={css.noAddressSelected}>
                    Выберите адрес доставки
                  </div>
                )}
                <div className={css.addressSelectorArrow}>›</div>
              </div>
            </div>

            {showAddressPopup && (
              <div className={css.addressPopup}>
                <div className={css.addressPopupBackdrop} onClick={() => setShowAddressPopup(false)} />
                <div className={css.addressPopupContent}>
                  <div className={css.addressPopupHeader}>
                    <h2>Выберите адрес</h2>
                    <button 
                      className={css.addressPopupClose}
                      onClick={() => setShowAddressPopup(false)}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className={css.addressPopupList}>
                    {addresses.length > 0 ? (
                      addresses.map((addr: any) => (
                        <div
                          key={addr.id}
                          className={`${css.addressPopupCard} ${address === addr.address ? css.addressPopupCardActive : ''}`}
                          onClick={() => {
                            setAddress(addr.address);
                            setShowAddressPopup(false);
                          }}
                        >
                          <div className={css.addressPopupCardContent}>
                            <div className={css.addressPopupCardTitle}>{addr.title}</div>
                            <div className={css.addressPopupCardAddress}>{addr.address}</div>
                          </div>
                          {address === addr.address && (
                            <div className={css.addressPopupCardCheck}>✓</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className={css.noAddressesMessage}>
                        У вас нет сохраненных адресов
                      </div>
                    )}
                    
                    <button
                      type="button"
                      className={css.createAddressBtn}
                      onClick={() => {
                        setShowAddressPopup(false);
                        navigate('/webapp/user', { state: { page: 4 } });
                      }}
                    >
                      + Создать адрес
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={css.total}>   
            <div className={css.item}>
              <a>Комментарий к заказу</a>
              <input type='text' name='comment' value={comment} onChange={(e) => setComment(e.target.value)} placeholder='Комментарий к заказу' />
            </div>
          </div>

          <div className={css.total}>
            <div className={css.itemFlex}>
              <a>Товары ({ cartCount }):</a>
              <b>{totalPrice.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</b>
            </div>

            <div className={css.itemFlex}>
              <a>Доставка:</a>
              <b>Без доплат</b>
            </div>

            <div className={`${css.item} ${css.totalPrice}`}>
              <a>Итоговая стоимость:</a>
              <b>{totalPrice.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</b>
            </div>
          </div>

          <button className={css.checkout} onClick={goToCheckout}>Оформить заказ</button>
        </div>
      </div>
    </MainContent>
    }
    </>
  )
}